import React, {useMemo, useEffect, lazy, Suspense, useState} from 'react';
import {Avatar, Card, Col, Divider, Layout, Row, Space, Typography, Table, Tabs, Descriptions} from 'antd';
import { useSelector } from 'react-redux';
import {cumsum} from "../../../utils/math";
import {Alert} from "@mui/material";
import {COOL_COLOR_PALETTE} from "../../../utils/colorUtils";
import StockChart from "../../../charts/StockChart";
import BiplotChart from "./BiplotChart";
import LoadingMatrix from "./LoadingMatrix";
import {useGptResponses} from "../../../hooks/useGptResponses";
import { parseGptResponse } from '../../../utils/gptUtils';
import FactorHeatmap from "./FactorHeatmap";
import {getIndexName} from "../../../utils/abbreviations";

import PcaFlow from "./PcaFlow";
import PcaInference from "./PcaInference";

const expectedResponseConfig = [
    {key: 'summary'},
    {key: 'portfolio', parse: true},
    {key: 'components', parse: true}
];

const transformDecompositionResults = (data)=>{
    const componentSeries = data?.component_series;
    const timestamps = data?.timestamps;
    const chartData = [];
    const biplotData = [];
    const nComponents = componentSeries[0].length;
    for (let j = 0; j < nComponents; j++) {
        const series = cumsum(componentSeries.map(x => x[j]));
        const name = `Component ${j + 1}`;
        const data = timestamps.map((timestamp, index) => [timestamp, series[index]]);
        chartData.push({name, data, stroke: COOL_COLOR_PALETTE?.[j], thickness: 2});
    }
    for (let k in data.loading_matrix){
        biplotData.push({
            title: k,
            data: data.loading_matrix[k],
        })
    }

    return {
        chartData: [
            {
                plot: 0,
                data: chartData,
                xGrid: false,
                yGrid: true,
                yGridStroke: '3 #f2f2f2',
                // yMinorGrid: true
                // ...anystockPercentagePlotConfi
            }
        ],
        biplotData
    }
}



const DecompDetails = ({data})=>{
    const { config } = data;
    const [startDate, endDate] = [
        config.start_date,
        config.end_date
    ];
    const tickersUsed = config.tickers;
    const factors = config?.factors;


    return (
        <Card bordered={false}>
            <Card bordered={false} className={'shadow-sm'} title={<Typography.Title level={4}>Model Details</Typography.Title>}>
                <Descriptions column={2}>
                    <Descriptions.Item label={'Start Date'}>{startDate}</Descriptions.Item>
                    <Descriptions.Item label={'End Date'}>{endDate}</Descriptions.Item>
                    <Descriptions.Item label={'# of Tickers'}>{tickersUsed.length}</Descriptions.Item>
                    <Descriptions.Item label={'# of Factors'}>{factors.length}</Descriptions.Item>
                </Descriptions>
            </Card>
            <Divider/>
            <Card bordered={false} className={'shadow-sm'} title={ <Typography.Title level={4}>Factors Used</Typography.Title>}>
                <Descriptions column={2}>
                    {factors.map((factor, index)=>(
                        <Descriptions.Item label={getIndexName(factor)}>
                            {factor.replace('^', '$').toUpperCase()}
                        </Descriptions.Item>

                    ))}
                </Descriptions>
            </Card>
        </Card>
    )

}

const DecompView = ({data})=>{
    const {chartData, biplotData} = useMemo(()=>transformDecompositionResults(data), [data]);
    const {submitPrompt, responseId, loadingResponse} = useGptResponses();
    const gptResponse = useSelector(state => state.gptApi.responses?.[responseId]);
    const parsedResponse = useMemo(()=>{
        if (gptResponse && gptResponse.hasOwnProperty('data')){
            return parseGptResponse(gptResponse.data, expectedResponseConfig);
        }
    }, [gptResponse])

    const [factorMatches, loadingMatrix] = [
        data?.factor_matches,
        data?.loading_matrix
    ];


    useEffect(()=>{
        if (!responseId){
            submitPrompt(JSON.stringify({factorMatches, loadingMatrix, tickerGroups: data?.grouped_features}), 'PCA', 'inferPcaResults')
        }
    }, [data]);


    return (
        <Layout style={{height: '100vh', overflowY: 'scroll', padding: '2.5%'}}>
            <Tabs centered={true}>
                <Tabs.TabPane key={'1'} tab={'Overview'}>
                    <Row align={'middle'} className={'mt-4'}>
                        <Col span={12} style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ flexGrow: 1 }}>
                                <DecompDetails data={data} />
                            </div>
                        </Col>

                    {/* Right Column: FactorHeatmap */}
                        <Col span={12} style={{ display: 'flex', flexDirection: 'column' }}>
                            <Card bordered={false} style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                <div style={{ width: '100%', flexGrow: 1 }}>
                                    <FactorHeatmap factorMatches={data.factor_matches} />
                                </div>
                            </Card>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <div className={'p-4'}>
                                <Typography.Title level={2}>
                                   <Avatar size={64} src={'https://storage.googleapis.com/finiat-web-static-resources/platformIcons/verified.jpg'}/> Recommended Ticker Groups
                                </Typography.Title>
                            </div>
                            <PcaFlow groups={data.grouped_features} />
                        </Col>
                    </Row>
                    <Divider/>
                    <Card bordered={false} title={<Typography.Title>Component Historical Series</Typography.Title>}>
                        <Typography.Paragraph>
                          The timeseries below represent statistical components extracted from the tickers aimed to
                            provide a 'maximum capture' of the variance in the data.
                        </Typography.Paragraph>
                    </Card>
                    <div style={{width: '100%'}} className={'px-1 py-3'}>
                        <StockChart dataSeries={chartData} height={600} />
                    </div>
                    <Divider/>
                    <LoadingMatrix data={data} />
                    <Divider/>
                </Tabs.TabPane>
                {gptResponse && (
                    <Tabs.TabPane key={'2'} tab={<Typography.Text style={{color:'var(--blue)',fontWeight:600}}>Insights</Typography.Text>}>
                        <PcaInference parsedResponse={parsedResponse} />
                    </Tabs.TabPane>)
                }
                <Tabs.TabPane key={'3'} tab={'Components'}>
                    <div style={{maxWidth: 1200, margin: 'auto', paddingTop: '2.5%'}}>
                        <Alert variant={'outlined'} color={'info'}>
                            The biplot below shows the relationship between the components and the tickers. The length of the vector represents the importance of the component in explaining the variance in the data. The angle between the vectors represents the correlation between the components.
                        </Alert>
                        <Divider/>
                        <div style={{height: '80vh', width: '90vw', margin: 'auto', minWidth: 700}} className={'p-3'}>
                            <BiplotChart loadingMatrices={biplotData} />
                        </div>
                    </div>
                </Tabs.TabPane>


            </Tabs>
        </Layout>
    )

}
export default DecompView;